/**
 * The shell every office screen renders inside.
 *
 * Holds the rail, the firm switcher, the running-timer indicator and the toast stack —
 * the four things that must survive navigation. The timer especially: it has to keep
 * ticking across screens, or a bookkeeper loses track of what they started.
 */
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppShell,
  FirmMark,
  Icon,
  MobileBar,
  NavRail,
  ShellContent,
  TimerIndicator,
  ToastStack,
} from "@onecpa/ui";
import { api } from "../api/client.js";
import { useAuth } from "../auth/AuthProvider.js";
import { activeNavId, MOBILE_ENTRIES, NAV_GROUPS, ROLE_LABELS } from "./navigation.js";
import { useSession } from "./session.js";
import { THEME_CYCLE, THEME_META, useTheme } from "./theme.js";

/**
 * Remembered rather than reset on every load: a bookkeeper who works on a laptop collapses
 * the rail once for the screen space, not once per session.
 */
const RAIL_COLLAPSED_KEY = "onecpa.railCollapsed";

export function Layout() {
  const session = useSession();
  const { signOutNow } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const [railCollapsed, setRailCollapsed] = useState(
    () => localStorage.getItem(RAIL_COLLAPSED_KEY) === "true",
  );

  const { choice: themeChoice, setTheme } = useTheme();
  const nextTheme = THEME_CYCLE[(THEME_CYCLE.indexOf(themeChoice) + 1) % THEME_CYCLE.length]!;

  const toggleRail = () => {
    setRailCollapsed((current) => {
      const next = !current;
      localStorage.setItem(RAIL_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const { data: timer } = useQuery({
    queryKey: ["time", "running", session.orgId],
    queryFn: () => api.time.running(session.orgId),
    // Polled rather than pushed: the elapsed figure has to advance on its own, and a
    // websocket for one counter would be a lot of machinery for a clock.
    refetchInterval: 30_000,
  });

  const stopTimer = useMutation({
    mutationFn: () => api.time.stop(session.orgId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["time"] });
      session.toast("הטיימר נעצר");
    },
  });

  // Ticks locally between polls so the minutes advance visibly.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!timer?.running) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [timer?.running]);

  const elapsedMinutes = timer?.running
    ? Math.max(0, Math.floor((now - new Date(timer.running.startedAt).getTime()) / 60_000))
    : 0;

  const activeId = activeNavId(pathname);

  return (
    <AppShell
      rail={
        <NavRail
          groups={NAV_GROUPS}
          role={session.role}
          activeId={activeId}
          onNavigate={(id) => void navigate({ to: id })}
          userName={session.me.user.fullName}
          roleLabel={ROLE_LABELS[session.role] ?? session.role}
          onSignOut={() => void signOutNow()}
          collapsed={railCollapsed}
          onToggleCollapsed={toggleRail}
          themeControl={
            /* One button cycling system → light → dark. The name announces both
               where it is and where it goes, because an icon-only tri-state
               control is otherwise a guess. */
            <button
              type="button"
              onClick={() => setTheme(nextTheme)}
              aria-label={`ערכת נושא: ${THEME_META[themeChoice].label}. מעבר ל${THEME_META[nextTheme].label}`}
              title={`ערכת נושא: ${THEME_META[themeChoice].label}`}
              className="text-night-ink transition-colors hover:text-on-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ledger-bright"
            >
              <Icon name={THEME_META[themeChoice].icon} className="size-4" />
            </button>
          }
          firm={{
            name: session.org.name,
            logoUrl: session.org.logoUrl,
            monogram: session.org.monogram,
          }}
          footer={
            session.organizations.length > 1 ? (
              <div className="border-t border-night-line px-3 py-2.5">
                <p className="px-1 pb-1.5 text-2xs font-semibold text-night-ink/60">
                  מעבר בין משרדים
                </p>
                {/* A list of marks rather than a select: the whole point of the
                    switcher is to make the destination recognisable before you
                    commit to it, and a native select shows one line of text with
                    no branding at all. */}
                <ul className="flex flex-col gap-0.5">
                  {session.organizations.map((org) => {
                    const active = org.id === session.orgId;
                    return (
                      <li key={org.id}>
                        <button
                          type="button"
                          onClick={() => session.switchOrg(org.id)}
                          aria-current={active ? "true" : undefined}
                          className={[
                            "flex w-full items-center gap-2 rounded-card px-1.5 py-1.5 text-start transition-colors",
                            "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ledger-bright",
                            active
                              ? "bg-white/10 font-semibold text-on-ink"
                              : "text-night-ink hover:bg-white/5",
                          ].join(" ")}
                        >
                          {/* Tiles, not wordmarks: several firms line up here, and
                              logos of different widths would leave the names on a
                              ragged edge that is harder to scan than the names
                              alone. */}
                          <FirmMark
                            firm={{
                              name: org.name,
                              logoUrl: org.logoUrl,
                              monogram: org.monogram,
                            }}
                            variant="tile"
                            size="sm"
                          />
                          <span className="min-w-0 flex-1 truncate text-xs">{org.name}</span>
                          {active && (
                            <span className="text-2xs text-ledger-bright" aria-hidden="true">
                              ●
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null
          }
        />
      }
    >
      <MobileBar
        title={session.orgName}
        entries={MOBILE_ENTRIES}
        activeId={activeId}
        onNavigate={(id) => void navigate({ to: id })}
        firm={{
          name: session.org.name,
          logoUrl: session.org.logoUrl,
          monogram: session.org.monogram,
        }}
      />

      {/* Outside the scrolling region on purpose: a running timer that scrolls out of view
          is a timer someone forgets, which is how an eight-hour entry happens. */}
      {timer?.running && (
        <div className="flex shrink-0 items-center gap-3 border-b border-rule bg-paper px-5 py-1.5">
          <TimerIndicator
            elapsedMinutes={elapsedMinutes}
            clientName={timer.running.clientName}
            onStop={() => stopTimer.mutate()}
          />
          <span className="truncate text-2xs text-ink-faint">{timer.running.description}</span>
        </div>
      )}

      <ShellContent>
        <Outlet />
      </ShellContent>

      <ToastStack messages={session.toasts} onDismiss={session.dismissToast} />
    </AppShell>
  );
}
